/**
 * Pure per-row + aggregate seat-math for a pool account — Seats/Assigned/Unassigned/Utilization.
 * Extracted from PoolAccountsPage.tsx (rev 9/10, 21-22/7) so the Dashboard's Overview stats (rev
 * 16) share the EXACT same null-handling and rounding as the per-account table, rather than a
 * second hand-written copy quietly drifting from it — same reasoning as poolAuditFormat.ts's own
 * banner (one pure module, every screen that needs this math imports it).
 *
 * "Assigned" (licensed_members) is never actually NULL over the wire — `list_pool_accounts`
 * computes it with `count(*)`, which returns 0, not NULL, when a pool has no licensed members yet.
 * The `?? 0` below documents that rather than defends against a real null; `PoolAccount`'s type
 * keeps it nullable only because standalone-account mock fixtures use null as an explicit
 * "no data seeded" placeholder.
 */
import { tierOf } from "@/lib/planNames";
import type { PoolAccount } from "@/api/types";

/** The four tiers tierOf() buckets every mirror-sourced (and, since rev 16, manually-entered)
 *  account_type into — the canonical display order for any by-tier breakdown. */
export const ACCOUNT_TIER_ORDER = ["Enterprise", "Business", "Pro", "Education"] as const;

/** Sentinel bucket key for an account whose account_type doesn't resolve to a known tier (never
 *  entered on a standalone account, or a not-yet-recognised mirror key) — always sorts last. */
export const UNKNOWN_TIER = "unknown";

/** Unassigned = Seats (hosts_total) − Assigned (licensed_members), the RAW difference — never
 *  clamped at 0 (rev 10, 22/7, principal decision: an oversold pool must be visible, not silently
 *  floored). Em dash (null) when Seats is unknown — a strict null check (unlike utilizationPct's
 *  `!r.hosts_total`, which also treats 0 seats as "unknown" to dodge a divide-by-zero): 0 is a
 *  real, meaningful seat count here, not a missing one. */
export function unassignedCount(
  r: Pick<PoolAccount, "hosts_total" | "licensed_members">,
): number | null {
  if (r.hosts_total == null) return null;
  return r.hosts_total - (r.licensed_members ?? 0);
}

/** null/0 seats → unknown utilization (never a false 0%) — mirrors the "no data, not zero"
 *  treatment hosts_total/licensed_members already get elsewhere. */
export function utilizationPct(
  r: Pick<PoolAccount, "hosts_total" | "licensed_members">,
): number | null {
  if (!r.hosts_total) return null;
  // Multiply BEFORE dividing — round_half_up parity with the API (`assigned*100.0/seats`).
  // Dividing first loses the .5 boundary to floating point: 23/40=0.575 but 23*100=2300 is exact,
  // so 23*100/40=57.5 rounds to 58 (matches the API) while (23/40)*100=57.49999999999999 rounds to
  // 57 (adversarial review, 31/7).
  return Math.round(((r.licensed_members ?? 0) * 100) / r.hosts_total);
}

export interface PoolStatAggregate {
  /** Every account in this group, known-seats or not — always exact. */
  accounts: number;
  /** Sum of hosts_total over accounts where it's known; null when NONE of them are (never a false
   *  0 — "—" on screen, same as a single unknown row). */
  seats: number | null;
  /** Sum of licensed_members (?? 0) over EVERY account in the group — always exact and complete,
   *  regardless of whether any account's Seats happens to be filled in yet (assigned membership is
   *  observed by the sweep independently of the manual account_type/seats entry). */
  assigned: number;
  /** seats − assigned, RAW (can go negative — same non-clamping rule as the per-row version,
   *  extended here to a second real cause: some of `assigned` may belong to an account whose seat
   *  count isn't recorded yet, which understates `seats` but not `assigned`). null when seats is. */
  unassigned: number | null;
  /** round(assigned * 100 / seats) — multiply-before-divide, round_half_up parity with the API;
   *  null when seats is unknown or 0. */
  utilizationPct: number | null;
}

/** The narrow shape aggregatePoolStats actually needs — widened from `PoolAccount` (30/7, Reports tab)
 *  so `PoolReportAccountRow` (a day-snapshot's own account row, api/types.ts) can share this EXACT
 *  formula without being coerced into the full live-account shape it isn't. `PoolAccount` still
 *  satisfies this structurally, so every existing call site (Dashboard) is unaffected. */
type StatRow = Pick<PoolAccount, "hosts_total" | "licensed_members">;

/** Aggregate Seats/Assigned/Unassigned/Utilization over a set of accounts — the same shape for a
 *  single tier bucket and for the grand total, so the Overview's Total tiles and its by-tier table
 *  read off one formula. */
export function aggregatePoolStats(rows: readonly StatRow[]): PoolStatAggregate {
  const known = rows.filter((r) => r.hosts_total != null);
  const seats = known.length > 0 ? known.reduce((sum, r) => sum + (r.hosts_total ?? 0), 0) : null;
  const assigned = rows.reduce((sum, r) => sum + (r.licensed_members ?? 0), 0);
  return {
    accounts: rows.length,
    seats,
    assigned,
    unassigned: seats == null ? null : seats - assigned,
    // Multiply BEFORE dividing — see utilizationPct() above for the .5-boundary rationale.
    utilizationPct: seats ? Math.round((assigned * 100) / seats) : null,
  };
}

/** The narrow shape bucketByTier actually needs — same widening reasoning as StatRow above. Generic
 *  over `T` (rather than fixed to `PoolAccount`) so the returned buckets keep the CALLER's full row
 *  type (e.g. `PoolReportAccountRow`, which the Reports day-view per-account table below still needs
 *  every field of) instead of collapsing to this narrow slice. */
type TierRow = Pick<PoolAccount, "account_type">;

/** Group accounts by tierOf(account_type), in ACCOUNT_TIER_ORDER + UNKNOWN_TIER last — omitting
 *  any tier with zero accounts (DESIGN.md §6: "hide a label/column that has nothing to show"). */
export function bucketByTier<T extends TierRow>(rows: readonly T[]): { tier: string; rows: T[] }[] {
  const buckets = new Map<string, T[]>();
  for (const r of rows) {
    // tierOf(null) is null by construction (its own guard clause) — every account with no
    // account_type at all (a standalone row not yet filled in) lands in UNKNOWN_TIER here.
    const key = tierOf(r.account_type) ?? UNKNOWN_TIER;
    const group = buckets.get(key);
    if (group) group.push(r);
    else buckets.set(key, [r]);
  }
  const order = [...ACCOUNT_TIER_ORDER, UNKNOWN_TIER];
  const known = order
    .filter((tier) => buckets.has(tier))
    .map((tier) => ({ tier, rows: buckets.get(tier) ?? [] }));
  const rest = [...buckets.entries()]
    .filter(([tier]) => !order.includes(tier as (typeof order)[number]))
    .map(([tier, tierRows]) => ({ tier, rows: tierRows }));
  return [...known, ...rest];
}

/** Tier bucket key → its display label — "Not set" for the UNKNOWN_TIER sentinel (a standalone
 *  account whose Account type was never filled in), the raw tier name otherwise. Shared by the
 *  Dashboard's by-tier breakdown AND the Reports day-view's, so the two tables read off one formula
 *  (30/7) — moved here from PoolDashboardPage.tsx, which now imports it instead of a local copy. */
export function tierLabel(t: (key: string) => string, tier: string): string {
  return tier === UNKNOWN_TIER ? t("pool.dashboard.overview.tierUnknown") : tier;
}
